import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { LageplanAnsicht } from '../types';
import lageplanService from '../services/lageplanService';
import exportService from '../services/exportService';
import { useFriedhof } from '../contexts/FriedhofContext';
import { abteilungColorMap } from '../components/lageplan/leafletUtils';
import { BASISKARTEN, DEFAULT_BASISKARTE } from '../components/lageplan/basiskarten';
import { Button, Card, PageHeader, ContextChip, LoadingScreen, Spinner, Icon } from '../components/ui';

const FALLBACK_COLOR = '#6b7280';

const UploadCloudIcon: React.FC<{ className?: string }> = ({ className = 'h-6 w-6' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 16l-4-4-4 4M12 12v9" />
    <path d="M20.4 14.5A5 5 0 0018 5.2 6.5 6.5 0 005.5 8 5 5 0 005 18" />
  </svg>
);
const TableIcon: React.FC<{ className?: string }> = ({ className = 'h-5 w-5' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9.5h18M3 15h18M9 4v16" />
  </svg>
);
const PaletteIcon: React.FC<{ className?: string }> = ({ className = 'h-5 w-5' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22a10 10 0 110-20c5.5 0 10 4 10 9 0 3-2.5 5-5 5h-2a2 2 0 00-1.5 3.3A1.5 1.5 0 0112 22z" />
    <circle cx="7.5" cy="10.5" r="1" fill="currentColor" stroke="none" />
    <circle cx="12" cy="7.5" r="1" fill="currentColor" stroke="none" />
    <circle cx="16.5" cy="10.5" r="1" fill="currentColor" stroke="none" />
  </svg>
);

const LayersIcon: React.FC<{ className?: string }> = ({ className = 'h-5 w-5' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2l9 5-9 5-9-5 9-5z" /><path d="M3 12l9 5 9-5" /><path d="M3 17l9 5 9-5" />
  </svg>
);

/** Kartenkopf: Icon-Kachel + Titel + Beschreibung, einheitlich über alle Bereiche. */
const SectionHeader: React.FC<{ icon: React.ReactNode; title: string; desc: string }> = ({ icon, title, desc }) => (
  <div className="flex items-start gap-4">
    <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-brand-50 text-brand-700 ring-1 ring-brand-100">
      {icon}
    </div>
    <div className="min-w-0">
      <h2 className="text-base font-semibold text-gray-900">{title}</h2>
      <p className="mt-0.5 text-sm leading-relaxed text-gray-500">{desc}</p>
    </div>
  </div>
);

// Admin-Bereich: pro gewähltem Friedhof Excel-Export, GeoJSON-Import der Grabgeometrie
// und die Konfiguration der Abteilungsfarben für die Lageplan-Ansicht „Nach Abteilung".
const Verwaltung: React.FC = () => {
  const { friedhofId, friedhofName } = useFriedhof();
  const [data, setData] = useState<LageplanAnsicht | null>(null);
  const [loading, setLoading] = useState(true);

  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [savingColors, setSavingColors] = useState(false);
  const [colorDraft, setColorDraft] = useState<Record<string, string>>({});
  const [hintergrundDraft, setHintergrundDraft] = useState<string>(DEFAULT_BASISKARTE);
  const [savingHintergrund, setSavingHintergrund] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    if (!friedhofId) { setLoading(false); return; }
    setLoading(true);
    try {
      setData(await lageplanService.getAnsicht(friedhofId));
    } catch {
      toast.error('Friedhofsdaten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [friedhofId]);

  useEffect(() => { load(); }, [load]);

  // Abteilungen + Auto-Palette aus den georeferenzierten Gräbern ableiten.
  const graeber = useMemo(
    () => (data?.graeber ?? []).filter(g => g.polygon && g.polygon.length >= 3),
    [data]
  );
  const autoColors = useMemo(() => abteilungColorMap(graeber), [graeber]);
  const sections = useMemo(() => Object.keys(autoColors), [autoColors]);
  const savedColors = useMemo(
    () => ({ ...autoColors, ...(data?.abteilungFarben ?? {}) }),
    [autoColors, data]
  );

  // Gespeicherte (bzw. Auto-)Farben in den Entwurf übernehmen, sobald die Daten geladen sind.
  useEffect(() => {
    const draft: Record<string, string> = {};
    sections.forEach(s => { draft[s] = savedColors[s] || FALLBACK_COLOR; });
    setColorDraft(draft);
  }, [sections, savedColors]);

  const colorsDirty = useMemo(
    () => sections.some(s => (colorDraft[s] || '').toLowerCase() !== (savedColors[s] || '').toLowerCase()),
    [sections, colorDraft, savedColors]
  );

  // Gespeicherte Standard-Hintergrundkarte in den Entwurf übernehmen, sobald die Daten geladen sind.
  const savedHintergrund = data?.lageplanHintergrund || DEFAULT_BASISKARTE;
  useEffect(() => { setHintergrundDraft(savedHintergrund); }, [savedHintergrund]);
  const hintergrundDirty = hintergrundDraft !== savedHintergrund;

  const handleExport = async () => {
    if (!friedhofId) return;
    setExporting(true);
    try {
      await exportService.downloadExcel(friedhofId);
      toast.success('Excel-Export heruntergeladen');
    } catch {
      toast.error('Excel-Export konnte nicht erstellt werden');
    } finally {
      setExporting(false);
    }
  };

  const importFile = useCallback(async (file: File) => {
    if (!friedhofId) return;
    setImporting(true);
    try {
      const text = await file.text();
      const featureCollection = JSON.parse(text);
      const result = await lageplanService.importGeoJson(friedhofId, featureCollection);
      toast.success(`Grabgeometrie importiert: ${result.angelegt} angelegt, ${result.aktualisiert} aktualisiert` +
        (result.uebersprungen ? `, ${result.uebersprungen} übersprungen` : ''));
      await load();
    } catch (error: any) {
      const msg = error?.response?.data?.error
        || (error instanceof SyntaxError ? 'Die Datei ist kein gültiges GeoJSON' : 'Grabgeometrie konnte nicht importiert werden');
      toast.error(msg);
    } finally {
      setImporting(false);
    }
  }, [friedhofId, load]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) importFile(file);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) importFile(file);
  };

  const handleSaveColors = async () => {
    if (!friedhofId) return;
    setSavingColors(true);
    try {
      await lageplanService.saveAbteilungFarben(friedhofId, colorDraft);
      toast.success('Abteilungsfarben gespeichert');
      await load();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Abteilungsfarben konnten nicht gespeichert werden');
    } finally {
      setSavingColors(false);
    }
  };

  const handleSaveHintergrund = async () => {
    if (!friedhofId) return;
    setSavingHintergrund(true);
    try {
      await lageplanService.saveLageplanHintergrund(friedhofId, hintergrundDraft);
      toast.success('Standard-Hintergrund gespeichert');
      await load();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Standard-Hintergrund konnte nicht gespeichert werden');
    } finally {
      setSavingHintergrund(false);
    }
  };

  if (!friedhofId) {
    return (
      <div className="mx-auto max-w-5xl p-6">
        <PageHeader title="Verwaltung" subtitle="Export, Import und Darstellung – zentral an einem Ort." />
        <Card className="border-dashed p-12 text-center">
          <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full bg-gray-100 text-gray-400">
            <Icon.MapPin />
          </div>
          <p className="font-medium text-gray-700">Kein Friedhof ausgewählt</p>
          <p className="mt-1 text-sm text-gray-500">Wählen Sie links einen Friedhof aus, um Export, Import und Darstellung zu verwalten.</p>
        </Card>
      </div>
    );
  }

  if (loading) return <LoadingScreen />;

  return (
    <div className="mx-auto max-w-5xl p-6">
      <PageHeader
        title="Verwaltung"
        subtitle="Export, Import und Darstellung – zentral an einem Ort."
        chip={friedhofName ? <ContextChip>{friedhofName}</ContextChip> : undefined}
      />

      <div className="grid gap-5 md:grid-cols-2">
        {/* Excel-Export */}
        <Card className="flex flex-col p-6 transition-shadow hover:shadow-md">
          <SectionHeader
            icon={<TableIcon />}
            title="Excel-Export"
            desc="Alle Daten dieses Friedhofs als Excel-Datei – je ein Blatt für Gräber, Tiere, Kunden und Verträge."
          />
          <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-gray-100 pt-5">
            <Button onClick={handleExport} loading={exporting} icon={<Icon.Download className="h-4 w-4" />}>
              {exporting ? 'Export läuft…' : 'Als Excel exportieren'}
            </Button>
            <span className="text-xs text-gray-400">.xlsx · {sections.length || 'keine'} Abteilung(en)</span>
          </div>
        </Card>

        {/* GeoJSON-Import */}
        <Card className="flex flex-col p-6 transition-shadow hover:shadow-md">
          <SectionHeader
            icon={<Icon.MapPin />}
            title="Grabgeometrie importieren"
            desc="GeoJSON-FeatureCollection mit den Grabpolygonen. Bestehende Gräber werden aktualisiert, neue angelegt – nichts wird gelöscht."
          />
          <div className="mt-5">
            <input
              ref={fileInputRef}
              type="file"
              accept=".geojson,application/geo+json,application/json"
              onChange={handleInputChange}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => !importing && fileInputRef.current?.click()}
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              disabled={importing}
              className={`flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-7 text-center transition-colors ${
                dragging
                  ? 'border-brand-500 bg-brand-50'
                  : 'border-gray-300 bg-gray-50/60 hover:border-brand-400 hover:bg-brand-50/40'
              } disabled:cursor-not-allowed disabled:opacity-70`}
            >
              <span className={`grid h-11 w-11 place-items-center rounded-full ${dragging ? 'bg-brand-100 text-brand-700' : 'bg-white text-gray-400 ring-1 ring-gray-200'}`}>
                {importing ? <Spinner className="h-5 w-5 text-brand-600" /> : <UploadCloudIcon />}
              </span>
              <span className="text-sm font-medium text-gray-700">
                {importing ? 'Importiere…' : 'Datei hierher ziehen oder auswählen'}
              </span>
              <span className="text-xs text-gray-400">.geojson / .json</span>
            </button>
          </div>
        </Card>

        {/* Standard-Hintergrundkarte */}
        <Card className="flex flex-col p-6 transition-shadow hover:shadow-md md:col-span-2">
          <SectionHeader
            icon={<LayersIcon />}
            title="Standard-Hintergrundkarte"
            desc="Welche Hintergrundkarte beim Öffnen des Lageplans voreingestellt ist. Mitarbeiter können sie über den Ebenen-Umschalter jederzeit selbst wechseln."
          />
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {BASISKARTEN.map(({ id, label }) => {
              const selected = hintergrundDraft === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setHintergrundDraft(id)}
                  className={`flex items-center gap-2 rounded-xl border p-3 text-left text-sm transition-colors ${
                    selected
                      ? 'border-brand-500 bg-brand-50 text-brand-800 ring-1 ring-brand-300'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className={`grid h-5 w-5 shrink-0 place-items-center rounded-full border ${selected ? 'border-brand-600' : 'border-gray-300'}`}>
                    {selected && <span className="h-2.5 w-2.5 rounded-full bg-brand-600" />}
                  </span>
                  <span className="min-w-0 truncate font-medium">{label}</span>
                </button>
              );
            })}
          </div>
          <div className="mt-5 flex flex-wrap items-center justify-end gap-3 border-t border-gray-100 pt-5">
            <span className="mr-auto text-xs text-gray-400">
              {hintergrundDirty ? 'Ungespeicherte Änderungen' : 'Alle Änderungen gespeichert'}
            </span>
            <Button onClick={handleSaveHintergrund} loading={savingHintergrund} disabled={!hintergrundDirty}>
              {savingHintergrund ? 'Speichere…' : 'Hintergrund speichern'}
            </Button>
          </div>
        </Card>

        {/* Abteilungsfarben */}
        <Card className="flex flex-col p-6 transition-shadow hover:shadow-md md:col-span-2">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <SectionHeader
              icon={<PaletteIcon />}
              title="Abteilungsfarben"
              desc="Farbe je Abteilung für die Lageplan-Ansicht „Nach Abteilung“. Gilt für alle Mitarbeiter."
            />
            {sections.length > 0 && (
              <span className="shrink-0 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-500">
                {sections.length} Abteilung(en)
              </span>
            )}
          </div>

          {sections.length === 0 ? (
            <div className="mt-5 rounded-xl border border-dashed border-gray-300 bg-gray-50/60 p-8 text-center text-sm text-gray-500">
              <p className="font-medium text-gray-700">Noch keine Abteilungen</p>
              <p className="mt-1">Die Abteilungen ergeben sich aus der importierten Grabgeometrie.</p>
              <div className="mt-4 flex justify-center">
                <Button
                  variant="secondary"
                  onClick={() => fileInputRef.current?.click()}
                  loading={importing}
                  icon={<Icon.Upload className="h-4 w-4" />}
                >
                  Grabgeometrie importieren
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {sections.map(s => {
                  const value = colorDraft[s] || FALLBACK_COLOR;
                  const isAuto = value.toLowerCase() === (autoColors[s] || '').toLowerCase();
                  return (
                    <div
                      key={s}
                      className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 transition-colors hover:border-gray-300"
                    >
                      <label className="relative h-10 w-10 shrink-0 cursor-pointer overflow-hidden rounded-lg shadow-inner ring-1 ring-black/5">
                        <span className="absolute inset-0" style={{ backgroundColor: value }} />
                        <input
                          type="color"
                          value={value}
                          onChange={e => setColorDraft(d => ({ ...d, [s]: e.target.value }))}
                          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                        />
                      </label>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold text-gray-900">Abteilung {s}</div>
                        <div className="font-mono text-xs uppercase text-gray-400">{value}</div>
                      </div>
                      <button
                        onClick={() => setColorDraft(d => ({ ...d, [s]: autoColors[s] }))}
                        disabled={isAuto}
                        className="rounded-md px-2 py-1 text-xs font-medium text-brand-700 transition-colors hover:bg-brand-50 disabled:opacity-40 disabled:hover:bg-transparent"
                        title="Auf automatische Farbe zurücksetzen"
                      >
                        Auto
                      </button>
                    </div>
                  );
                })}
              </div>

              <div className="mt-5 flex flex-wrap items-center justify-end gap-3 border-t border-gray-100 pt-5">
                <span className="mr-auto text-xs text-gray-400">
                  {colorsDirty ? 'Ungespeicherte Änderungen' : 'Alle Änderungen gespeichert'}
                </span>
                <Button onClick={handleSaveColors} loading={savingColors} disabled={!colorsDirty}>
                  {savingColors ? 'Speichere…' : 'Farben speichern'}
                </Button>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Verwaltung;
